"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.syntheticDocumentSchema = void 0;
exports.parseSyntheticDocument = parseSyntheticDocument;
// `config/synthetic/<name>.yaml` (SPEC §14.4, §10.1): what the generator needs for the organisation files
// it writes, which are the clinicians of each practice and the identity prefixes. Practice names refer to
// `organization.yaml` practices and are checked there by `synthetic-cross-check.ts`. NPI registration is
// not a building block (D-1012).
//
// J-184 (the owner's decision, 2026-10-12): the patient settings left with the F1 and F2 files. A
// document stored before then may still hold them, so each retired key is accepted and dropped on
// parse; any other unknown key is refused as before.
const zod_1 = require("zod");
const config_issue_js_1 = require("./config-issue.js");
const countSchema = zod_1.z.int().nonnegative();
const practiceNameSchema = zod_1.z.string().min(1);
/** The keys J-184 retired, by the object that held them. */
const RETIRED = {
    document: ["encounter_date_range", "anomalies", "scale"],
    identity: ["name_variant_rate"],
    defaults: [
        "age_range",
        "measure_age_ranges",
        "measure_rates",
        "exception_rate",
        "exclusion_rate",
        "completeness",
        "deceased_rate",
    ],
    practice: [
        "mode",
        "patients",
        "exact_counts",
        "unregistered_patients",
        "overlap_with",
        "documented_elsewhere",
        "age_range",
        "measure_age_ranges",
        "measure_rates",
        "exception_rate",
        "exclusion_rate",
        "completeness",
    ],
};
/** `shape` as a strict object that also accepts the retired `keys` and drops them from its output. */
function withRetired(shape, keys) {
    const retired = Object.fromEntries(keys.map((key) => [key, zod_1.z.unknown().optional()]));
    return zod_1.z.strictObject({ ...shape, ...retired }).transform((value) => {
        const kept = { ...value };
        for (const key of keys)
            delete kept[key];
        return kept;
    });
}
function rangeSchema(bound) {
    return zod_1.z
        .tuple([bound, bound])
        .refine(([low, high]) => low <= high, "the low end of a range is above its high end");
}
const cliniciansSchema = rangeSchema(zod_1.z.int().min(1));
const identitySchema = withRetired({
    tin_prefix: zod_1.z.string().regex(/^\d{1,8}$/, "tin_prefix is 1 to 8 digits"),
    npi_prefix: zod_1.z.string().regex(/^\d{1,9}$/, "npi_prefix is 1 to 9 digits"),
    first_names_file: zod_1.z.string().min(1),
    last_names_file: zod_1.z.string().min(1),
}, RETIRED.identity);
const practiceSchema = withRetired({
    practice: practiceNameSchema,
    clinicians: cliniciansSchema.optional(),
    // SPEC §10.3 S2: clinicians billing under the practice's TINs that no group registers.
    unregistered_clinicians: countSchema.optional(),
}, RETIRED.practice);
function checkUniquePractices(doc, ctx) {
    const seen = new Set();
    doc.practices.forEach(({ practice }, index) => {
        if (!seen.has(practice))
            seen.add(practice);
        else {
            const message = `practice ${practice} is already listed`;
            ctx.addIssue({ code: "custom", path: ["practices", index, "practice"], message });
        }
    });
}
exports.syntheticDocumentSchema = withRetired({
    seed: zod_1.z.int().nonnegative(),
    performance_year: zod_1.z.int().min(1000).max(9999),
    banner_text: zod_1.z.string().min(1),
    identity: identitySchema,
    defaults: withRetired({ clinicians: cliniciansSchema }, RETIRED.defaults),
    practices: zod_1.z.array(practiceSchema).min(1),
}, RETIRED.document).superRefine(checkUniquePractices);
/** Parses `config/synthetic/<name>.yaml`; issues name the document `synthetic/<name>`. */
function parseSyntheticDocument(name, input) {
    return (0, config_issue_js_1.parseConfigDocument)(exports.syntheticDocumentSchema, `synthetic/${name}`, input);
}
//# sourceMappingURL=synthetic.js.map